/* React side of formRecovery.js. */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  armDraft, finishRestore, getVersion, pendingRestore, subscribe,
} from './formRecovery'

function useRecoveryVersion() {
  return useSyncExternalStore(subscribe, getVersion, getVersion)
}

function useLatest(value) {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  })
  return ref
}

function onDraftRoute(draft, location) {
  return `${location.pathname}${location.search}` === draft.route
}

/**
 * Wire one form into failed-write recovery. Call it in the component that
 * owns the form's open state and fields, so it is mounted while the form
 * could be reopened:
 *
 *   const arm = useFormRecovery('jobs.create', (values) => { setForm(values); setOpen(true) })
 *   ...on submit: arm(form); create.mutate(form)
 *
 * `key` must be unique per form. `onRestore` receives exactly what was armed.
 * `arm(values, context)` — context is only for hosts (useRecoveryHost). Never
 * arm passwords or tokens.
 */
export function useFormRecovery(key, onRestore) {
  const version = useRecoveryVersion()
  const location = useLocation()
  const restoreRef = useLatest(onRestore)

  useEffect(() => {
    const draft = pendingRestore()
    if (!draft || draft.key !== key || !onDraftRoute(draft, location)) return
    finishRestore()
    restoreRef.current(draft.values)
  }, [version, location, key, restoreRef])

  return useCallback((values, context) => armDraft(key, values, context), [key])
}

/**
 * For a container that has to open before the form inside it can mount — a
 * screen that shows a modal, a profile that has to switch tab. Fires `onHost`
 * with the draft's context when a pending restore's key starts with `prefix`;
 * it does NOT consume the draft, the form's own useFormRecovery does that once
 * it mounts. Keys are dotted, e.g. 'candidateProfile.note'.
 */
export function useRecoveryHost(prefix, onHost) {
  const version = useRecoveryVersion()
  const location = useLocation()
  const hostRef = useLatest(onHost)

  useEffect(() => {
    const draft = pendingRestore()
    if (!draft || !draft.key.startsWith(prefix) || !onDraftRoute(draft, location)) return
    hostRef.current(draft.context ?? {}, draft.key)
  }, [version, location, prefix, hostRef])
}

/** Mounted once inside the router: takes the user back to the failed form's page. */
export function FormRecoveryNavigator() {
  const version = useRecoveryVersion()
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useLatest(location)

  useEffect(() => {
    const draft = pendingRestore()
    // Location is read, not tracked: re-running on every navigation would keep
    // pulling the user back if the form's owner never mounts.
    if (draft && !onDraftRoute(draft, locationRef.current)) navigate(draft.route)
  }, [version, navigate, locationRef])

  return null
}
